import type { ProjectLink } from '@lixionary-jira/domain';
import { ExternalLink } from 'lucide-react';
import { ApiError } from '../../api/client';
import { Button } from '../../ui/Button';
import { Field, Input } from '../../ui/forms';

/** Shared project metadata editor for creation and settings. */
export function ProjectLinksEditor({
  links,
  onChange,
  disabled = false,
  error,
}: {
  links: ProjectLink[];
  onChange: (links: ProjectLink[]) => void;
  disabled?: boolean;
  error?: Error | null;
}) {
  const apiError = error instanceof ApiError ? error : null;
  const update = (index: number, field: keyof ProjectLink, value: string) =>
    onChange(links.map((link, i) => (i === index ? { ...link, [field]: value } : link)));
  return (
    <fieldset className="project-links-editor stack" disabled={disabled}>
      <legend>
        Project links <span className="small muted">· Optional</span>
      </legend>
      <p className="small muted">Add named links to dashboards, documentation, or other project pages.</p>
      {links.map((link, index) => (
        <div className="project-link-editor-row" key={index}>
          <Field label={`Link ${index + 1} name`} error={apiError?.fieldError(`links.${index}.name`)}>
            {(f) => (
              <Input
                {...f}
                value={link.name}
                onChange={(e) => update(index, 'name', e.target.value)}
                placeholder="Main dashboard"
                required
                maxLength={120}
              />
            )}
          </Field>
          <Field label={`Link ${index + 1} URL`} error={apiError?.fieldError(`links.${index}.url`)}>
            {(f) => (
              <Input
                {...f}
                type="url"
                value={link.url}
                onChange={(e) => update(index, 'url', e.target.value)}
                placeholder="https://test.lixionary.com"
                required
                maxLength={2048}
              />
            )}
          </Field>
          <Button
            variant="ghost"
            aria-label={`Remove link ${index + 1}`}
            onClick={() => onChange(links.filter((_, i) => i !== index))}
          >
            Remove
          </Button>
        </div>
      ))}
      {apiError?.details.find((d) => d.field === 'links') && (
        <span className="error-text">{apiError.fieldError('links')}</span>
      )}
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={links.length >= 20}
          onClick={() => onChange([...links, { name: '', url: '' }])}
        >
          Add link
        </Button>
      </div>
    </fieldset>
  );
}

/** Full destinations stay visible so members can identify the site before opening it. */
export function ProjectLinks({ links }: { links: ProjectLink[] }) {
  if (!links.length) return null;
  return (
    <section className="project-links" aria-label="Project links">
      <h2>Project links</h2>
      <ul>
        {links.map((link, index) => (
          <li key={index}>
            <span className="strong">{link.name}:</span>{' '}
            <a href={link.url} target="_blank" rel="noopener noreferrer">
              {link.url} <ExternalLink size={13} aria-hidden />
              <span className="visually-hidden"> (opens in a new tab)</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
